/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// React
import React, {useState, useEffect, useRef} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

// PWA Kit OOTB Componets
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {
    Box,
    Skeleton,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@app/components/scroll-to-top'
import ScrollToHash from '@app/components/scroll-to-hash'
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import RedirectWithStatus from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/redirect-with-status'

// Custom Components
import OfflineBanner from '../offline-banner'
import Header from '../header'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import Footer from '../footer'
import {DrawerMenu} from '../drawer-menu'
import AboveHeader from './partials/above-header'
import {CountryDispatch} from '../../pages/country-dispatch'
import CountrySelectorModal from '../country-selector-modal'
import LocaleSelectorModal from '@app/components/locale-selector-modal'
import CookieConsentBanner from '../cookie-consent-banner'
import SeoLinks from '../seo-links'
import LoadingSpinner from '@app/components/loading-spinner'

// Commerce SDK Imports
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation,
    useShopperContext,
    useUsid
} from '@salesforce/commerce-sdk-react'

// Chakra
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '../../contexts'
import {
    ConversionRateProvider,
    IPProvider,
    PasswordProvider,
    useDeliveryCountryDecision
} from '../../custom-contexts'

// PWA Kit OOTB Hooks
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '../../hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Custom Hooks
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {useDntNotification} from '@salesforce/retail-react-app/app/hooks/use-dnt-notification'
import {AddToCartModalProvider} from './../../hooks/use-add-to-cart-modal'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import useUpdateShopperContext from '../../hooks/use-update-shopper-context'
import useResetShippingAddress from '../../hooks/use-reset-shipping-address'
import useSiteType from '../../hooks/use-site-type'
import useUTMParameters from '../../hooks/use-utm-parameters'
import useRiskified from '../../hooks/use-riskified'
import useRiskifiedBeacon from '../../hooks/use-riskified-beacon'
import {useExplicitShipToCountry} from '@app/hooks/use-explicit-ship-to-country'
import {useToast} from '../../hooks/use-toast'

// Utils
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {watchOnlineStatus, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getShipToCountry} from '../../utils/ship-to-country-utils'
import {
    getLocalStorageJSONItem,
    setLocalStorageJSONItem,
    cookieSettings,
    getAppDomain
} from '../../utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getDefaultShipment, useInitializeBasketMgr} from '../../utils/basket-utils'

// Third Party
import {IntlProvider, FormattedMessage} from 'react-intl'
import {useQuery, useIsFetching} from '@tanstack/react-query'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import Cookies from 'js-cookie'

// Constants
import {
    DEFAULT_SITE_TITLE,
    THEME_COLOR,
    CAT_MENU_DEFAULT_ROOT_CATEGORY,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '@salesforce/retail-react-app/app/constants'
import {
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    GTM_SCRIPT_URL,
    GTM_NO_SCRIPT_URL,
    OUTLET_ENTRANCE_URL,
    OUTLET_LIMITATION_EXCEPTIONS,
    OUTLET_DISPATCH_URL,
    WORLDWIDE_SHIPPING_HREF,
    OUTLET_SESSION_PARAM,
    UNLIMITED_OUTLET_SESSION,
    ACCESSIBILITY_URL,
    GORGIAS_CHAT_WIDGET_URL,
    GORGIAS_CHAT_BUNDLE_URL,
    GORGIAS_CHAT_WIDGET_KEY,
    GORGIAS_CHAT_BUNDLE_KEY,
    COOKIE
} from '../../constants'

// Styles
import './global.css'

import {getGlobalRedirectionURL, getMainDomainURL} from '../../utils/seo-utils'
import {useOutletCampaignStatus} from '../../utils/outlet-utils'
import OutletEntrance from '../../pages/outlet-entrance'

/**
 * Persists the SSR-resolved ship-to country in the browser after hydration.
 *
 * The SSR server cannot set this cookie in Managed Runtime responses, so the
 * browser writes the technical country cookie after hydration.
 *
 * @param {string} countryCode - Resolved ship-to country code from the delivery decision.
 */
const persistDecisionShipToCountryCookie = (countryCode) => {
    if (isServer || !countryCode) return
    if (Cookies.get(COOKIE.SHIP_TO_COUNTRY) === countryCode) return

    Cookies.set(COOKIE.SHIP_TO_COUNTRY, countryCode, {
        expires: 365,
        path: '/',
        sameSite: 'Lax'
    })
}

const ExplicitShipToCountryHandler = ({setIsSetShipToCountryEnabled}) => {
    const history = useHistory()
    const location = useLocation()
    const toast = useToast()

    useExplicitShipToCountry({
        history,
        location,
        onApplied: () => {
            setIsSetShipToCountryEnabled(true)
        },
        onError: (error) => {
            logger.error('Error applying explicit ship-to country', {
                namespace: 'App.useExplicitShipToCountry',
                additionalProperties: {
                    errorMessage: error.message
                }
            })

            toast({
                title: 'An error occurred.',
                description: error.message,
                status: 'error'
            })
        }
    })

    return null
}

ExplicitShipToCountryHandler.propTypes = {
    setIsSetShipToCountryEnabled: PropTypes.func.isRequired
}

const App = (props) => {
    const {children} = props
    const {app: appConfig} = getConfig()
    const GTM_ID = appConfig.GTM_ID
    const appOrigin = useAppOrigin()
    const frontAppDomain = getAppDomain()
    const {site, locale, buildUrl} = useMultiSite()
    const deliveryCountryDecision = useDeliveryCountryDecision()
    const {getTokenWhenReady} = useAccessToken()
    const {usid} = useUsid()
    const {data: categoriesTree} = useCategory({
        parameters: {
            id: CAT_MENU_DEFAULT_ROOT_CATEGORY,
            levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
            c_isMenu: true
        }
    })
    const {riskifiedBeacon, riskifiedDomain} = useRiskified(site?.id)
    const basketMgr = useInitializeBasketMgr()

    useEffect(() => {
        const updateLocale = async () => {
            try {
                const accessToken = await getTokenWhenReady()
                const result = await basketMgr.updateProductLineItemsLocale(
                    accessToken,
                    site.id,
                    locale.id
                )
                if (result?.updated) {
                    await refetchBasket()
                }
            } catch (error) {
                console.error('Error updating locale:', error)
            }
        }

        if (locale?.id) {
            updateLocale()
        }
    }, [locale?.id, site.id])
    // Get IP Address from the server if the app is running on the server.
    const {data: ipData} = useQuery({
        queryKey: ['userIP'],
        queryFn: async () => {
            const token = await getTokenWhenReady()
            const response = await fetch(
                `${appOrigin}/block-status?localeId=${locale?.id}&siteId=${site?.id}&orgId=${appConfig?.commerceAPI?.parameters.organizationId}`,
                {
                    method: 'GET',
                    headers: {
                        authorization: token
                    }
                }
            )
            const result = await response.json()

            return result
        },
        enabled: !isServer
    })

    // Cookie consent handling uses 3 states:
    // - null: consent status has not been checked in localStorage yet, so the banner must not render
    // - false: consent status was checked and no user decision exists, so show the banner
    // - true: user has already accepted or declined cookies, so keep the banner hidden
    const [areCookiesHandled, setAreCookiesHandled] = useState(null)

    // logic for showing/hiding the current modal
    const [activeModal, setActiveModal] = useState(null)
    const [hasHandledCountryModal, setHasHandledCountryModal] = useState(false)
    const [hasHandledLocaleModal, setHasHandledLocaleModal] = useState(false)
    const [, setIsSetShipToCountryEnabled] = useState(null)
    const cspNonce = appConfig?.commerceAPI?.parameters?.cspNonce
    // GTM Integration
    useEffect(() => {
        if (!isServer && cspNonce) {
            ;(function (w, d, s, l, i) {
                w[l] = w[l] || []
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js'
                    //'gtm.nonce': `${cspNonce}`
                })
                var f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l != 'dataLayer' ? '&l=' + l : ''
                j.async = true
                j.nonce = `${cspNonce}`
                j.src = GTM_SCRIPT_URL + i + dl
                f.parentNode.insertBefore(j, f)
            })(window, document, 'script', 'dataLayer', GTM_ID)
        }
    }, [cspNonce])

    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )

    /**
     * Sets the local storage items related to cookie consent.
     *
     * This function updates the local storage to indicate whether cookies have been handled
     * and whether the user has accepted cookies. It stores these values as strings.
     *
     * @param {boolean} consent - A boolean value indicating whether the user has consented to cookies.
     *                             If true, 'cookiesAccepted' is set to 'true'; otherwise, it is set to an empty string.
     */
    const setLocalStorage = (consent) => {
        setLocalStorageJSONItem('cookiesHandled', 'true')
        setLocalStorageJSONItem('cookiesAccepted', consent ? 'true' : 'false')
    }

    /**
     * Handles the user's cookie consent decision.
     *
     * This function is triggered when the user provides their consent for cookies.
     * If consent is given, it updates the state to indicate that cookies have been handled
     * and configures the settings for various types of cookies, including Google Analytics (ga),
     * advertising (ad), and social networking (sn) cookies.
     *
     * @param {boolean} consent - A boolean value indicating whether the user has consented to cookies.
     * @param {boolean} isInitialLoad - A boolean indicating whether this is the initial load of the application. Defaults to false.
     */
    const handleCookieConsent = (consent, isInitialLoad = false) => {
        if (!isServer) {
            const consentTypes = ['ga', 'ad', 'sn']
            const consentValue = consent ? 'true' : 'false'

            consentTypes.forEach((cookieType) =>
                cookieSettings(consentValue, cookieType, isInitialLoad)
            )

            if (!isInitialLoad) {
                setLocalStorage(consent)
            }

            setAreCookiesHandled(!!getLocalStorageJSONItem('cookiesHandled'))
        }
    }

    useEffect(() => {
        const cookiesHandled = !!getLocalStorageJSONItem('cookiesHandled')

        if (cookiesHandled) {
            handleCookieConsent(getLocalStorageJSONItem('cookiesAccepted') === 'true', true)
        } else {
            setAreCookiesHandled(false)
        }
    }, [])

    useEffect(() => {
        if (!isServer) {
            window.dataLayer = window.dataLayer || []
        }
    }, [])

    // reset shipping address if ship to country is different than the shipping address country
    useResetShippingAddress()

    const activeData = useActiveData()
    const history = useHistory()
    const location = useLocation()

    // hash loader on app
    const {hash} = useLocation()
    const isFetching = useIsFetching()
    const isHashLoader = hash && isFetching > 0

    const authModal = useAuthModal()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dntNotification = useDntNotification()
    const {isUS, isOutlet} = useSiteType()

    const {updateShopperContextAfterLogout} = useUpdateShopperContext({
        persistOnShipToCountryChange: true
    })

    const shopperContext = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )
    // handle UTM parameters with local storage
    useUTMParameters()

    useEffect(() => {
        // if we have a new accessToken and the shopper context is not yet set - we are forcing to set it
        if (!isOutletEntrancePage && !activeModal && shopperContext.error) {
            updateShopperContextAfterLogout(true)
        }
    }, [shopperContext.error])

    const countryCode = shopperContext?.data?.geoLocation?.countryCode
    const isWorldwideShippingPage = location?.pathname === WORLDWIDE_SHIPPING_HREF
    const isOutletDispatchPage = location?.pathname === buildUrl(OUTLET_DISPATCH_URL) && isOutlet
    const isDispatchPage = isWorldwideShippingPage || isOutletDispatchPage
    const isOutletEntrancePage = location?.pathname === buildUrl(OUTLET_ENTRANCE_URL) && isOutlet
    const isPDP =
        location?.pathname.includes('/product/') ||
        location?.pathname.includes('/ps/') ||
        location?.pathname.includes('/p/')

    const {shipToCountry, setShipToCountry} = useShipToCountry()
    const shopperContextShipToCountry = getShipToCountry(countryCode, isUS)
    const acceptedCookieCountryId = !isServer ? Cookies.get(COOKIE.SHIP_TO_COUNTRY_ACCEPTED) : null
    const acceptedCookieShipToCountry = getShipToCountry(acceptedCookieCountryId, isUS)
    const acceptedCookieMatchesSite =
        acceptedCookieCountryId &&
        acceptedCookieShipToCountry?.id &&
        (acceptedCookieShipToCountry.id === 'US') === isUS
    const effectiveDecisionShipToCountry = acceptedCookieMatchesSite
        ? acceptedCookieShipToCountry
        : deliveryCountryDecision?.shipToCountry
    const effectiveDecisionShipToCountryId = effectiveDecisionShipToCountry?.id
    const shouldShowCountrySelectorModal =
        deliveryCountryDecision?.shouldShowCountryModal && !acceptedCookieMatchesSite

    useEffect(() => {
        if (isDispatchPage) return

        persistDecisionShipToCountryCookie(effectiveDecisionShipToCountryId)
    }, [effectiveDecisionShipToCountryId, isDispatchPage])

    useEffect(() => {
        if (
            !isDispatchPage &&
            effectiveDecisionShipToCountry?.id &&
            shipToCountry?.id !== effectiveDecisionShipToCountry.id
        ) {
            setShipToCountry(effectiveDecisionShipToCountry)
        }
    }, [
        effectiveDecisionShipToCountry?.id,
        effectiveDecisionShipToCountry?.currency,
        isDispatchPage,
        shipToCountry?.id
    ])

    useEffect(() => {
        if (
            countryCode &&
            (!effectiveDecisionShipToCountryId ||
                countryCode === effectiveDecisionShipToCountryId) &&
            shopperContextShipToCountry?.id &&
            shipToCountry?.id !== shopperContextShipToCountry.id
        ) {
            setShipToCountry(shopperContextShipToCountry)
        }
    }, [
        countryCode,
        effectiveDecisionShipToCountryId,
        shopperContextShipToCountry?.id,
        shipToCountry?.id
    ])

    useRiskifiedBeacon({riskifiedDomain, riskifiedBeacon, shipToCountryId: shipToCountry?.id})

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')

    // Drawer Menu
    const {isOpen, onOpen, onClose} = useDisclosure()
    const drawerMenuTriggerRef = useRef()

    const {
        isOpen: isOpenStoreLocator,
        onOpen: onOpenStoreLocator,
        onClose: onCloseStoreLocator
    } = useDisclosure()

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', locale?.id],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(locale?.id, appOrigin)
        },
        keepPreviousData: true,
        enabled: isServer || (!isServer && isDispatchPage)
    })

    // Used to conditionally render for checkout page
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = shipToCountry?.currency || locale?.preferredCurrency || l10n?.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()

    // export conversion rate fields from basket to conversion rate provider
    const conversionRateFields = {
        conversionRate: basket?.c_conversionRate,
        lcpMultiplier: basket?.c_lcpMultiplier,
        paymentCurrencyCode: basket?.c_paymentCurrencyCode,
        usePriceConverter: basket?.c_usePriceConverter || false,
        internationalShipping: basket?.c_internationalShipping || false
    }

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    const isOutletException = OUTLET_LIMITATION_EXCEPTIONS.some((exception) => {
        return location.pathname.includes(buildUrl(exception))
    })

    // handling outlet redirections
    const {isCampaignActive: isOutletOpened, isLoading: isCampaignStatusLoading} =
        useOutletCampaignStatus()

    const isToHideDrawerMenu = isOutlet && !isOutletOpened

    useEffect(() => {
        if (
            isOutlet &&
            ((!isCampaignStatusLoading && isOutletOpened === false) ||
                getLocalStorageJSONItem(OUTLET_SESSION_PARAM) !== UNLIMITED_OUTLET_SESSION) &&
            !isOutletException
        ) {
            window.location.href = buildUrl(OUTLET_ENTRANCE_URL)
        }
    }, [location, isCampaignStatusLoading, isOutletOpened])

    useEffect(() => {
        const updateBasketCurrency = async () => {
            // Check if the basket currency differs from the current locale currency
            if (basket?.currency && basket.currency !== currency) {
                // Update the basket with the new currency
                updateBasket.mutate({
                    parameters: {basketId: basket.basketId},
                    body: {currency}
                })

                // Retrieve the default shipment for the basket
                const defaultShipment = getDefaultShipment(basket)

                // Reset the shipping address for the default shipment
                await updateShippingAddressForShipment.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: defaultShipment?.shipmentId
                    },
                    body: {}
                })
            }
        }

        updateBasketCurrency()
    }, [shipToCountry?.currency, shipToCountry?.id])
    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email &&
            basket?.basketId
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [
        customer?.isRegistered,
        customer?.email,
        basket?.customerInfo?.email,
        basket && basket?.basketId
    ])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onAccountClick = () => {
        // Link to account page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account')
        history.push(path)
    }

    const onWishlistClick = () => {
        // Link to wishlist page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account/wishlist')
        history.push(path)
    }

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    /**
     * useEffect hook to control the display of application modals based on various state conditions.
     *
     * This effect determines which modal should be active by evaluating the following conditions:
     * - If the SSR delivery decision requires a country or locale confirmation, the related modal is activated.
     * - If cookies have not been handled, the 'cookieConsent' modal is activated.
     * - If none of the above conditions are met, no modal is shown.
     *
     * The effect re-runs whenever any of the following dependencies change:
     * - areCookiesHandled: Boolean indicating if cookie consent has been managed.
     * - isDispatchPage: Boolean indicating if the current page is a dispatch page.
     *
     * @function
     * @memberof App
     */
    useEffect(() => {
        if (
            !isDispatchPage &&
            !isOutletEntrancePage &&
            shouldShowCountrySelectorModal &&
            !hasHandledCountryModal
        ) {
            setActiveModal('countrySelector')
        } else if (
            !isDispatchPage &&
            !isOutletEntrancePage &&
            deliveryCountryDecision?.shouldShowLocaleModal &&
            !hasHandledLocaleModal
        ) {
            setActiveModal('localeSelector')
        } else if (areCookiesHandled === false && !isOutletEntrancePage) {
            // Only show the cookie banner after the consent status has been checked in localStorage
            // areCookiesHandled is initially `null`, so we must explicitly compare with `false`
            setActiveModal('cookieConsent')
        } else {
            setActiveModal(null)
        }
    }, [
        areCookiesHandled,
        deliveryCountryDecision?.shouldShowLocaleModal,
        hasHandledCountryModal,
        hasHandledLocaleModal,
        isDispatchPage,
        isOutletEntrancePage,
        shouldShowCountrySelectorModal
    ])

    /*
        Since we need to create canonical tags on every page manage it in the _app component.
        When it is needed overwrite the default canonical tag in the component itself by adding <Helmet> like below when it is needed.
        <Helmet>
            <link rel="canonical" href="/overwritten-canonical-tag-goes-here" />
        </Helmet>
    */
    const isSearchPage = location.pathname.includes('/search')
    const [defaultCanonicalTag, setDefaultCanonicalTag] = useState(
        new URL(location.pathname, frontAppDomain).toString()
    )

    useEffect(() => {
        setDefaultCanonicalTag(new URL(location.pathname, frontAppDomain).toString())
    }, [location])

    let realOrigin = appOrigin

    const {req} = useServerContext()
    if (isServer) {
        const host = req?.headers?.['x-forwarded-host']
        if (host) {
            realOrigin = `https://${host}`
            const mainDomainURL = getMainDomainURL(
                appConfig.PWA_DOMAIN_SITE,
                realOrigin,
                location,
                appConfig.DOMAIN_REDIRECT_PATHS
            )
            if (mainDomainURL) {
                return <RedirectWithStatus status={301} to={mainDomainURL} />
            }
        }
    }

    // GORGIAS CHAT
    useEffect(() => {
        if (isServer) return

        const gorgiasInjectionTimer = setTimeout(() => {
            // 1. Check if script already exists to prevent duplicates$
            var check = document.querySelector('#gorgias-chat-widget-install-v3')
            if (check) {
                if (
                    check.getAttribute('data-site') == site.id &&
                    check.getAttribute('data-locale') == locale.id
                ) {
                    return // already declared for the same site and locale
                } else {
                    check.remove() // site and/or locale has change, removing so a new instance is created
                }
            }

            const gorgiasWidget = document.createElement('script')
            gorgiasWidget.id = 'gorgias-chat-widget-install-v3'
            gorgiasWidget.src =
                GORGIAS_CHAT_WIDGET_URL + GORGIAS_CHAT_WIDGET_KEY[site.id][locale.id]
            gorgiasWidget.setAttribute('data-site', site.id)
            gorgiasWidget.setAttribute('data-locale', locale.id)
            document.body.appendChild(gorgiasWidget)

            const gorgiasBundle = document.createElement('script')
            gorgiasBundle.src =
                GORGIAS_CHAT_BUNDLE_URL + GORGIAS_CHAT_BUNDLE_KEY[site.id][locale.id]
            gorgiasBundle.async = true
            document.body.appendChild(gorgiasBundle)
        }, 10000) // 10 seconds

        // 2. Cleanup: Clear the timeout if the component unmounts
        return () => clearTimeout(gorgiasInjectionTimer)
    }, [site, locale])

    const globalRedirectionUrl = getGlobalRedirectionURL(location)
    if (globalRedirectionUrl) {
        return <RedirectWithStatus status={301} to={globalRedirectionUrl} />
    }

    // Display a skeleton while outlet campaign is retrieved on outlet sites
    if (isOutlet && !isOutletEntrancePage && !isOutletException && isCampaignStatusLoading) {
        return <Skeleton height="100vh" width="100%" />
    }

    return (
        <Box className="sf-app" {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                            defer
                        ></script>
                    )}
                    {/*  <Site24x7 /> */}
                    {appConfig.ENABLE_SITE24X7 && (
                        <script
                            src={getAssetUrl('static/24x7.js')}
                            id="24x7_script"
                            type="text/javascript"
                            defer
                        ></script>
                    )}
                    <script>
                        {`
                    if (window.trustedTypes) {
                        window.trustedTypes.createPolicy('default', {
                            createHTML: (string) => string,
                            createScript: (string) => string,
                            createScriptURL: (string) => string
                        });
                    }
                    `}
                    </script>
                    <html lang={locale?.id} />
                </Helmet>
                {!isPDP && <SeoLinks />}
                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }
                        if (err.code === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the console error for missing translations during development,
                            // as we knew translations would be added later.
                            logger.warn('Missing translation', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        } else if (err.code === 'FORMAT_ERROR') {
                            logger.warn('Format error', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        } else {
                            logger.warn(err.code, {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        }
                    }}
                    locale={locale?.id}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <CurrencyProvider currency={currency}>
                        <ExplicitShipToCountryHandler
                            setIsSetShipToCountryEnabled={setIsSetShipToCountryEnabled}
                        />
                        <PasswordProvider>
                            <ConversionRateProvider conversionRateFields={conversionRateFields}>
                                <IPProvider
                                    ip={ipData?.clientIp}
                                    hasFetched={!!ipData?.clientIp}
                                    isIPBlocked={ipData?.isIPBlocked}
                                >
                                    <Seo>
                                        {!isSearchPage && !isPDP && !isWorldwideShippingPage && (
                                            <link rel="canonical" href={defaultCanonicalTag} />
                                        )}
                                        <meta name="theme-color" content={THEME_COLOR} />
                                        <meta
                                            name="apple-mobile-web-app-title"
                                            content={DEFAULT_SITE_TITLE}
                                        />

                                        {/* Favicon list */}
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-16x16.png'
                                            )}
                                            sizes="16x16"
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-32x32.png'
                                            )}
                                            sizes="32x32"
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-96x96.png'
                                            )}
                                            sizes="96x96"
                                        />
                                        {/* older-iPhone */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon.png'
                                            )}
                                        />
                                        {/* iPhone-6-Plus */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-180x180.png'
                                            )}
                                            sizes="180x180"
                                        />
                                        {/* iPad-Retina */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-152x152.png'
                                            )}
                                            sizes="152x152"
                                        />
                                        {/* iPad-Pro */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-167x167.png'
                                            )}
                                            sizes="167x167"
                                        />
                                        <link
                                            rel="manifest"
                                            href={getAssetUrl('static/manifest.json')}
                                        />

                                        {/* Styles from external tools */}
                                        {/* Use media queries to load CSS conditionally to improve performance. */}
                                        <link
                                            rel="stylesheet"
                                            type="text/css"
                                            href={getAssetUrl('static/global.css')}
                                        />
                                        {!isCheckout && (
                                            <link
                                                rel="stylesheet"
                                                type="text/css"
                                                href={getAssetUrl(
                                                    'static/third-party/slick-carousel/slick/slick.css'
                                                )}
                                            />
                                        )}
                                        {!isCheckout && (
                                            <link
                                                rel="stylesheet"
                                                type="text/css"
                                                href={getAssetUrl(
                                                    'static/third-party/slick-carousel/slick/slick-theme.css'
                                                )}
                                            />
                                        )}
                                    </Seo>

                                    <ScrollToTop />
                                    <ScrollToHash />

                                    <Box id="app" display="flex" flexDirection="column" flex={1}>
                                        {activeModal === 'cookieConsent' && (
                                            <CookieConsentBanner
                                                handleCookieConsent={handleCookieConsent}
                                            />
                                        )}
                                        <Link
                                            href={ACCESSIBILITY_URL}
                                            zIndex="skipLink"
                                            {...styles.hiddenLink}
                                        >
                                            <FormattedMessage
                                                defaultMessage="ACCESSIBILITY"
                                                id="skip_link.accessibility"
                                            />
                                        </Link>
                                        <SkipNavLink zIndex="skipLink" {...styles.hiddenLink}>
                                            <FormattedMessage
                                                defaultMessage="SKIP TO MAIN CONTENT"
                                                id="skip_link.skip_content"
                                            />
                                        </SkipNavLink>

                                        {isDispatchPage && (
                                            <SkipNavContent>
                                                <CountryDispatch></CountryDispatch>
                                                <Footer simplified={true} />
                                            </SkipNavContent>
                                        )}
                                        {!isDispatchPage && (
                                            <>
                                                <StoreLocatorModal
                                                    isOpen={isOpenStoreLocator}
                                                    onClose={onCloseStoreLocator}
                                                />

                                                {activeModal === 'countrySelector' && (
                                                    <CountrySelectorModal
                                                        setIsSetShipToCountryEnabled={
                                                            setIsSetShipToCountryEnabled
                                                        }
                                                        onDismiss={() => {
                                                            setHasHandledCountryModal(true)
                                                            setActiveModal(null)
                                                        }}
                                                    />
                                                )}
                                                {activeModal === 'localeSelector' && (
                                                    <LocaleSelectorModal
                                                        isOpen={true}
                                                        onClose={() => {
                                                            setHasHandledLocaleModal(true)
                                                            setActiveModal(null)
                                                        }}
                                                    />
                                                )}

                                                <Box {...styles.headerWrapper}>
                                                    {!isOutletEntrancePage &&
                                                        (!isCheckout ? (
                                                            <>
                                                                <AboveHeader />
                                                                <Header
                                                                    onMenuClick={onOpen}
                                                                    onMyAccountClick={
                                                                        onAccountClick
                                                                    }
                                                                    onWishlistClick={
                                                                        onWishlistClick
                                                                    }
                                                                    onStoreLocatorClick={
                                                                        onOpenStoreLocator
                                                                    }
                                                                    drawerMenuTriggerRef={
                                                                        drawerMenuTriggerRef
                                                                    }
                                                                    hideNavigationControls={
                                                                        isToHideDrawerMenu
                                                                    }
                                                                >
                                                                    {!isToHideDrawerMenu && (
                                                                        <DrawerMenu
                                                                            isOpen={isOpen}
                                                                            onClose={onClose}
                                                                            root={categoriesTree}
                                                                            itemsKey="categories"
                                                                            itemsCountKey="onlineSubCategoriesCount"
                                                                            drawerMenuTriggerRef={
                                                                                drawerMenuTriggerRef
                                                                            }
                                                                        />
                                                                    )}
                                                                </Header>
                                                            </>
                                                        ) : (
                                                            <CheckoutHeader />
                                                        ))}
                                                </Box>
                                                {!isOnline && <OfflineBanner />}
                                                <AddToCartModalProvider>
                                                    <SkipNavContent
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: 1,
                                                            outline: 0
                                                        }}
                                                    >
                                                        <Box
                                                            as="main"
                                                            id="app-main"
                                                            role="main"
                                                            display="flex"
                                                            flexDirection="column"
                                                            flex="1"
                                                        >
                                                            {isHashLoader && (
                                                                <LoadingSpinner
                                                                    isBodyLocked={true}
                                                                    wrapperStyles={{
                                                                        height: '100dvh'
                                                                    }}
                                                                />
                                                            )}

                                                            <OfflineBoundary isOnline={false}>
                                                                {isOutletEntrancePage ? (
                                                                    <OutletEntrance
                                                                        isOutletOpened={
                                                                            isOutletOpened
                                                                        }
                                                                        isCampaignStatusLoading={
                                                                            isCampaignStatusLoading
                                                                        }
                                                                    />
                                                                ) : (
                                                                    children
                                                                )}
                                                            </OfflineBoundary>
                                                        </Box>
                                                    </SkipNavContent>
                                                    {!isOutletEntrancePage && <Footer />}
                                                    <AuthModal {...authModal} />
                                                    {/* Commenting out as for the moment DNT is set to false by default */}
                                                    {/* <DntNotification {...dntNotification} /> */}
                                                </AddToCartModalProvider>
                                            </>
                                        )}
                                    </Box>
                                </IPProvider>
                            </ConversionRateProvider>
                        </PasswordProvider>
                    </CurrencyProvider>
                </IntlProvider>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        defer
                        onLoad={trackPage}
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        defer
                    ></script>
                )}
                <noscript>
                    <iframe
                        src={`${GTM_NO_SCRIPT_URL}${GTM_ID}`}
                        height="0"
                        width="0"
                        style={{display: 'none', visibility: 'hidden'}}
                        title="Google Tag Manager"
                    ></iframe>
                </noscript>
            </StorefrontPreview>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

export default App
